/**
 * These schemas validate strategy data from the API. Keep API nulls here and
 * convert them to editor-friendly values only in the strategies feature adapter.
 */
import { z } from "zod";

export const TradingPeriodSchema = z.enum(["PreMarket", "MarketHours"]);
export const ChartTypeSchema = z.enum(["Price", "Custom"]);
export const SeriesTypeSchema = z.enum(["Candlestick", "Histogram", "Line"]);

export const StrategyDetailsSchema = z.object({
  id: z.string(),
  isPublished: z.boolean(),
  name: z.string(),
  description: z.string(),
  tradingPeriod: TradingPeriodSchema,
  subStrategies: z.record(z.string(), z.object({ name: z.string() }).strict()),
}).strict();

export const StrategyDetailsListSchema = z.array(StrategyDetailsSchema);

const ChartVariableSchema = z.object({
  name: z.string(),
  variableExpression: z.string(),
  includeTimestampExpression: z.string(),
  seriesType: SeriesTypeSchema,
}).strict();

const TrendLineSchema = z.object({
  barAExpr: z.string(),
  barAExtensionExpr: z.string(),
  priceAExpr: z.string(),
  barBExpr: z.string(),
  barBExtensionExpr: z.string(),
  priceBExpr: z.string(),
}).strict();

export const StrategySettingsSchema = z.object({
  isFundamentalsAndEconomicConditions: z.boolean(),
  variablesCode: z.string(),
  criteriaCode: z.string(),
  entryCode: z.string().nullable(),
  subStrategies: z.record(
    z.string(),
    z.object({ variablesCode: z.string() }).strict(),
  ),
  trendLines: z.array(TrendLineSchema),
  chartBarCountExpr: z.string().nullable(),
  chartType: ChartTypeSchema,
  chartHighlightedBarCount: z.number().int(),
  customChartVariable: ChartVariableSchema.nullable(),
  overlaidChartVariables: z.array(ChartVariableSchema),
  subChartVariables: z.array(ChartVariableSchema),
  highlightedVariableName: z.string().nullable(),
  highlightedVariableExpr: z.string().nullable(),
}).strict();

export const StrategyRecordSchema = z.object({
  details: StrategyDetailsSchema,
  settings: StrategySettingsSchema,
}).strict();

export const StrategyRecordsSchema = z.array(StrategyRecordSchema);

export const StrategyUpsertSchema = StrategyDetailsSchema.omit({ id: true });

export const StrategyWriteSchema = z.object({
  details: StrategyUpsertSchema,
  settings: StrategySettingsSchema,
}).strict();

export type StrategyDetails = z.infer<typeof StrategyDetailsSchema>;
export type StrategyRecord = z.infer<typeof StrategyRecordSchema>;
export type StrategySettingsContract = z.infer<typeof StrategySettingsSchema>;
export type StrategyUpsert = z.infer<typeof StrategyUpsertSchema>;
export type StrategyWrite = z.infer<typeof StrategyWriteSchema>;
